/**
 * Penelope's settings editor, shared by the full panel (/penelope), the Penelope
 * tab of the trade ticket and the /live strip: the lean selector and the knobs
 * of one TIER (basic = the eight BASIC_KNOB_KEYS, advanced = every knob in KNOB_GROUPS with the
 * variant picker and clipboard). Controlled: the caller supplies the config and
 * a `save` that PATCHes (or edits a draft before the game exists).
 */
import { useState } from 'react';
import { cn } from '@/lib/infra/utils';
import { isVariantSpec, variantName, type PenelopeConfig, type PenelopeLean, type PenelopeVariant, type PenelopeVariantSpec } from '@/hooks/usePenelope';
import { VARIANT_GROUPS } from './knobs';
import { BASIC_KNOB_KEYS, KNOB_GROUPS, PENELOPE_GROUP_CLIPBOARD, PENELOPE_GROUP_VARIANTS, groupDomId, knobGroupsFor, knobMatches, shortHint, type Knob, type KnobDisplay, type KnobStrategy } from './knobs';
import { isChanged } from './baseline';

/** Knob keys an overlay may carry: everything the editor lists (never enabled / mode / variants / lean). */
const OVERLAY_KEYS: ReadonlySet<string> = new Set(KNOB_GROUPS.flatMap((g) => g.knobs.map((k) => k.key)));
export const MAX_VARIANTS = 8;

const UNKNOWN = '?';

/** A box's legal range per numeric knob (GET /api/penelope/profile `bounds`): the inputs carry the real ceilings. */
export type PenelopeKnobBounds = Record<string, { min: number; max: number }>;

/** A stored value as shown (R9 display units), and back. Shown values are rounded so 0.6 reads 60 %, not 59.999…. */
const toShown = (v: number, d?: KnobDisplay): number => (d ? Number((v / d.scale).toFixed(6)) : v);
const toStored = (x: number, d?: KnobDisplay): number => (!d ? x : d.scale >= 1 ? Math.round(x * d.scale) : Number((x * d.scale).toFixed(9)));
/** A value for "default X": the select's own label, a bool's on/off, a number in its shown unit. */
function valueText(k: Knob, v: unknown): string {
  if (k.kind === 'bool') return v === true ? 'on' : 'off';
  if (k.kind === 'select') return k.options.find((o) => o.value === v)?.label ?? String(v);
  if (v === null && k.nullable) return 'off';
  return typeof v === 'number' ? `${toShown(v, k.display)}${k.display?.unit ?? k.unit ?? ''}` : String(v);
}

export function KnobRow({ k, cfg, editable, save, bound, fieldError, base }: {
  k: Knob; cfg: PenelopeConfig; editable: boolean; save: (patch: Partial<PenelopeConfig>) => void;
  /** This box's range for the knob, over the knob's own typo bounds. */
  bound?: { min: number; max: number };
  /** The relay's refusal of this field on the last write. */
  fieldError?: string;
  /** R9: what one-click Start would run (knobBaseline). A knob set differently is marked, with its default and a reset. */
  base?: Partial<PenelopeConfig> | null;
}) {
  const v = cfg[k.key];
  const id = `penelope-knob-${k.key}`;
  const [problem, setProblem] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const display = k.kind === 'number' ? k.display : undefined;
  const nullable = k.kind === 'number' && k.nullable === true;
  const min0 = k.kind === 'number' ? (bound?.min ?? k.min) : undefined;
  const max0 = k.kind === 'number' ? (bound?.max ?? k.max) : undefined;
  // Bounds and steps are stored units; the input works in the shown ones.
  const min = min0 === undefined ? undefined : toShown(min0, display);
  const max = max0 === undefined ? undefined : toShown(max0, display);
  const step = k.kind === 'number' && k.step !== undefined ? toShown(k.step, display) : undefined;
  const changed = isChanged(cfg, base, k.key);
  const short = shortHint(k);
  const hintId = `${id}-hint`;
  // A knob another setting switches off (max behind under "at the best bid"): greyed, with the reason, never hidden —
  // the saved value is kept for when the other setting changes back.
  const unused = k.disabledWhen?.(cfg as unknown as Record<string, unknown>) ?? null;
  const canEdit = editable && unused === null;
  let input;
  if (k.kind === 'bool') {
    input = <input id={id} type="checkbox" aria-describedby={hintId} disabled={!canEdit} checked={v === true} onChange={(e) => save({ [k.key]: e.target.checked })} />;
  } else if (k.kind === 'select') {
    input = (
      <select id={id} aria-describedby={hintId} disabled={!canEdit} value={typeof v === 'string' ? v : ''} onChange={(e) => save({ [k.key]: e.target.value })} className="border-border bg-background rounded border px-1 py-0.5 font-mono">
        {typeof v !== 'string' && <option value="">{UNKNOWN}</option>}
        {k.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else {
    input = (
      <span className="flex items-center gap-1 font-mono">
        <input
          id={id}
          type="number"
          aria-describedby={hintId}
          disabled={!canEdit}
          step={step}
          min={min}
          max={max}
          defaultValue={typeof v === 'number' ? toShown(v, display) : ''}
          key={`${k.key}:${String(v)}`}
          placeholder={typeof v === 'number' ? undefined : v === null && nullable ? 'off' : UNKNOWN}
          aria-invalid={problem !== null || fieldError !== undefined ? true : undefined}
          onBlur={(e) => {
            const shown = Number(e.target.value);
            // A nullable knob (Josh 10-29 review): emptying the box switches it off.
            if (e.target.value === '' && nullable && typeof v === 'number') { setProblem(null); save({ [k.key]: null }); return; }
            if (e.target.value === '' || (typeof v === 'number' && shown === toShown(v, display))) { setProblem(null); return; }
            // Out of range is said, never silently dropped (Update 01: a value the box would not run is refused visibly).
            if (!Number.isFinite(shown)) { setProblem('not a number'); return; }
            if ((min !== undefined && shown < min) || (max !== undefined && shown > max)) {
              setProblem(`${min ?? '…'}–${max ?? '…'}${display?.unit ?? ''} on this box`);
              return;
            }
            setProblem(null);
            save({ [k.key]: toStored(shown, display) });
          }}
          className={cn('border-border bg-background w-20 rounded border px-1 py-0.5 text-right', (problem || fieldError) && 'border-red-500')}
        />
        <span className="text-muted-foreground w-5">{display?.unit ?? k.unit ?? ''}</span>
      </span>
    );
  }
  const def = base?.[k.key];
  return (
    <div className={cn('flex items-start justify-between gap-2 py-0.5', unused !== null && 'opacity-60')} data-unused={unused !== null ? 'true' : undefined} data-changed={changed ? 'true' : undefined}>
      <div className="flex min-w-0 flex-col">
        <label htmlFor={id}>
          {k.label}
          {changed && <span className="ml-1 text-sky-500" title={`changed from your default (${valueText(k, def)})`} aria-label="changed from default">•</span>}
        </label>
        {/* R9: one line by default; the whole hint is one click away. Outside the label, so "more" never toggles a box. */}
        <span id={hintId} className="text-muted-foreground text-[10px]">
          {more || short === k.hint ? k.hint : short}
          {short !== k.hint && (
            <button type="button" aria-expanded={more} onClick={() => setMore((x) => !x)} className="hover:text-foreground ml-1 underline decoration-dotted">
              {more ? 'less' : 'more'}
            </button>
          )}
        </span>
        {changed && (
          <span className="text-muted-foreground text-[10px]">
            default {valueText(k, def)}
            {canEdit && (
              <button type="button" onClick={() => save({ [k.key]: def })} className="ml-1 text-sky-500 underline decoration-dotted" aria-label={`reset ${k.label} to ${valueText(k, def)}`}>
                reset
              </button>
            )}
          </span>
        )}
        {unused !== null && <span className="text-[10px] text-amber-600">{unused}</span>}
        {(problem || fieldError) && <span role="alert" className="text-[10px] text-red-500">{problem ?? fieldError}</span>}
      </div>
      {input}
    </div>
  );
}

/** The variants that run beside a game: the built-in one-factor ones by group, and the researcher's own overlays
 *  (name + the knobs it changes, as JSON). `save` receives the whole new list. */
export function VariantPicker({ variants, editable, busy = false, save }: {
  variants: PenelopeVariant[];
  editable: boolean;
  busy?: boolean;
  save: (variants: PenelopeVariant[]) => void;
}) {
  const [name, setName] = useState('');
  const [overlay, setOverlay] = useState('');
  const [legs, setLegs] = useState<PenelopeVariantSpec['legs']>('noNo');
  const [problem, setProblem] = useState<string | null>(null);
  const names = new Set(variants.map(variantName));
  const custom = variants.filter(isVariantSpec);
  const full = variants.length >= MAX_VARIANTS;
  const add = () => {
    const n = name.trim();
    if (!/^[a-z0-9._-]{1,32}$/.test(n)) return setProblem('name: 1–32 of a-z 0-9 . _ -');
    if (names.has(n) || n === 'baseline') return setProblem('that name is taken');
    let parsed: unknown;
    try {
      parsed = overlay.trim() === '' ? {} : JSON.parse(overlay);
    } catch {
      return setProblem('overlay must be JSON, e.g. {"minEdgeCents": 1, "maxBehindCents": 3}');
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return setProblem('overlay must be a JSON object');
    const keys = Object.keys(parsed as Record<string, unknown>);
    const unknown = keys.filter((k) => !OVERLAY_KEYS.has(k));
    if (unknown.length > 0) return setProblem(`not a knob: ${unknown.join(', ')}`);
    if (keys.length === 0 && legs === 'noNo') return setProblem('an overlay has to change something (a knob, or the legs)');
    setProblem(null);
    save([...variants, { name: n, overlay: parsed as Record<string, unknown>, ...(legs && legs !== 'noNo' ? { legs } : {}) }]);
    setName('');
    setOverlay('');
    setLegs('noNo');
  };
  const chip = 'rounded border px-1 py-px font-mono text-[10px]';
  return (
    <div className="flex flex-col gap-1">
      {VARIANT_GROUPS.map((grp) => (
        <div key={grp.label} className="mb-1 flex flex-wrap items-center gap-1">
          <span className="text-muted-foreground w-36 text-[10px]">{grp.label}</span>
          {grp.names.map((n) => {
            const on = names.has(n);
            return (
              <button
                key={n}
                type="button"
                disabled={!editable || busy || (!on && full)}
                aria-pressed={on}
                onClick={() => save(on ? variants.filter((v) => variantName(v) !== n) : [...variants, n])}
                className={cn(chip, on ? 'border-sky-500 text-sky-500' : 'border-border/60 text-muted-foreground hover:text-foreground')}
              >
                {n}
              </button>
            );
          })}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-muted-foreground w-36 text-[10px]" title="Your own overlays: the game's settings with these knobs changed, run beside it in simulation">your own</span>
        {custom.length === 0 && <span className="text-muted-foreground text-[10px]">none yet</span>}
        {custom.map((v) => (
          <span key={v.name} className={cn(chip, 'border-violet-500 text-violet-500 flex items-center gap-1')} title={`${JSON.stringify(v.overlay)}${v.legs && v.legs !== 'noNo' ? ` · legs ${v.legs}` : ''}`}>
            {v.name}
            {editable && (
              <button type="button" disabled={busy} aria-label={`remove variant ${v.name}`} onClick={() => save(variants.filter((x) => variantName(x) !== v.name))} className="hover:text-foreground">
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {editable && (
        <div className="flex flex-wrap items-end gap-1" aria-label="Add a custom variant">
          <label className="flex flex-col text-[10px]">
            <span className="text-muted-foreground">name</span>
            <input value={name} disabled={busy || full} onChange={(e) => setName(e.target.value)} placeholder="edge1.behind3" className="border-border bg-background w-28 rounded border px-1 py-0.5 font-mono" />
          </label>
          <label className="flex flex-1 flex-col text-[10px]">
            <span className="text-muted-foreground">overlay (JSON of knobs to change)</span>
            <input value={overlay} disabled={busy || full} onChange={(e) => setOverlay(e.target.value)} placeholder='{"minEdgeCents": 1, "maxBehindCents": 3}' className="border-border bg-background min-w-48 rounded border px-1 py-0.5 font-mono" />
          </label>
          <label className="flex flex-col text-[10px]">
            <span className="text-muted-foreground">legs</span>
            <select value={legs ?? 'noNo'} disabled={busy || full} onChange={(e) => setLegs(e.target.value as PenelopeVariantSpec['legs'])} className="border-border bg-background rounded border px-1 py-0.5 font-mono">
              <option value="noNo">NO + NO</option>
              <option value="yesYes">YES + YES</option>
              <option value="oneMarket">one market</option>
            </select>
          </label>
          <button type="button" disabled={busy || full} onClick={add} className="border-border rounded border px-2 py-0.5 text-[10px] font-bold uppercase">
            add variant
          </button>
          {full && <span className="text-muted-foreground text-[10px]">max {MAX_VARIANTS} on a game</span>}
          {problem && <span role="alert" className="text-[10px] text-red-500">{problem}</span>}
        </div>
      )}
    </div>
  );
}

/** Copy the settings as JSON, or paste JSON over them (a preset of your own). Only knobs are pasted — never
 *  `enabled`, `mode` or `variants`. */
export function ConfigClipboard({ cfg, editable, busy = false, save }: { cfg: PenelopeConfig; editable: boolean; busy?: boolean; save: (patch: Partial<PenelopeConfig>) => void }) {
  const [note, setNote] = useState<string | null>(null);
  const knobsOnly = (c: Record<string, unknown>): Partial<PenelopeConfig> => Object.fromEntries(Object.entries(c).filter(([k]) => OVERLAY_KEYS.has(k) || k === 'lean'));
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(knobsOnly(cfg), null, 2));
      setNote('copied');
    } catch {
      setNote('could not reach the clipboard');
    }
  };
  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = JSON.parse(text) as Record<string, unknown>;
      if (typeof parsed !== 'object' || parsed === null) throw new Error('not an object');
      const patch = knobsOnly(parsed);
      if (Object.keys(patch).length === 0) throw new Error('no knobs in it');
      save(patch);
      setNote(`pasted ${Object.keys(patch).length} setting(s)`);
    } catch (e) {
      setNote(`paste failed: ${(e as Error).message}`);
    }
  };
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <button type="button" onClick={() => void copy()} className="border-border/60 text-muted-foreground hover:text-foreground rounded border px-1.5 py-px font-mono" title="Copy every knob as JSON — keep it as your own preset">
        copy JSON
      </button>
      {editable && (
        <button type="button" disabled={busy} onClick={() => void paste()} className="border-border/60 text-muted-foreground hover:text-foreground rounded border px-1.5 py-px font-mono" title="Paste JSON knobs over this game's settings (never arms it or changes its mode)">
          paste JSON
        </button>
      )}
      {note && <span className="text-muted-foreground">{note}</span>}
    </div>
  );
}

/** Penelope's two tiers (2026-10-08). `basic` is what every trader gets: the eight knobs in
 *  BASIC_KNOB_KEYS, flat, with "Max Long" driving the lean cap too (one cap to reason about), no variants, no
 *  clipboard. `advanced` is every knob in collapsible groups (the first two open) plus variants and the clipboard.
 *  `advanced-only` is `advanced` minus what `basic` already shows — for a disclosure rendered UNDER a basic block,
 *  so no knob (and its input id) appears twice on one screen. */
export type PenelopeTier = 'basic' | 'advanced' | 'advanced-only';

/** The knobs one tier shows for a strategy under a search and the changed filter (R9) — what PenelopeSettings renders
 *  and what the editor counts. */
export function visibleKnobs(o: { tier: PenelopeTier; strategy?: KnobStrategy; query?: string; onlyChanged?: boolean; cfg: Partial<PenelopeConfig>; base?: Partial<PenelopeConfig> | null }): Array<{ title: string; knobs: Knob[] }> {
  const wanted = (k: Knob): boolean => (o.tier === 'basic' ? BASIC_KNOB_KEYS.has(k.key) : o.tier === 'advanced-only' ? !BASIC_KNOB_KEYS.has(k.key) : true);
  const shown = (k: Knob): boolean => wanted(k) && knobMatches(k, o.query ?? '') && (!o.onlyChanged || isChanged(o.cfg, o.base, k.key));
  return knobGroupsFor(o.strategy ?? 'penelope').map((g) => ({ title: g.title, knobs: g.knobs.filter(shown) })).filter((g) => g.knobs.length > 0);
}

export function PenelopeSettings({ cfg, editable, busy = false, save, teamA, teamB, tier = 'advanced', bounds, fieldErrors, strategy = 'penelope', query = '', onlyChanged = false, base, openGroups, onToggleGroup, changedByGroup }: {
  cfg: PenelopeConfig;
  editable: boolean;
  busy?: boolean;
  save: (patch: Partial<PenelopeConfig>) => void;
  teamA: string;
  teamB: string;
  tier?: PenelopeTier;
  /** This box's ranges (GET /api/penelope/profile), so an input past the ceiling says so before the relay refuses it. */
  bounds?: PenelopeKnobBounds;
  /** The relay's per-field refusals of the last write. */
  fieldErrors?: Array<{ field: string; error: string }>;
  /** The row's strategy: its own group shows, another strategy's does not (default Penelope's). */
  strategy?: KnobStrategy;
  /** R9: show only the knobs matching this search (label, key, hint, group, alias). */
  query?: string;
  /** R9: show only the knobs set differently from `base`. */
  onlyChanged?: boolean;
  /** R9: what one-click Start would run (knobBaseline); marks each changed knob with its default and a reset. */
  base?: Partial<PenelopeConfig> | null;
  /** StreamRift mode (2026-10-20): which groups are open (the editor's memory). Absent = today's rule, the first two. */
  openGroups?: ReadonlySet<string> | null;
  /** StreamRift mode: a trader opened or closed a group by its summary (not while filtering). */
  onToggleGroup?: (title: string, open: boolean) => void;
  /** StreamRift mode: knobs per group set differently from `base` (changedByGroup); a count after the summary. */
  changedByGroup?: ReadonlyMap<string, number>;
}) {
  const errorOf = (key: string): string | undefined => fieldErrors?.find((e) => e.field === key)?.error;
  const basic = tier === 'basic';
  const filtering = query.trim() !== '' || onlyChanged;
  const leans: Array<{ value: PenelopeLean; label: string }> = [{ value: 'a', label: teamA }, { value: 'neutral', label: 'no lean' }, { value: 'b', label: teamB }];
  const groups = visibleKnobs({ tier, strategy, query, onlyChanged, cfg, base });
  // Basic has one "Max Long": the lean cap follows it, so a lean never has a second cap the trader did not set — unless
  // an advanced lean set its own (R9: a basic Max Long edit no longer overwrites a different Lean Max Long).
  const leanFollows = cfg.lean === undefined || cfg.lean === 'neutral' || cfg.leanMaxNetContracts === undefined || cfg.leanMaxNetContracts === cfg.maxNetContracts;
  const saveTiered = (patch: Partial<PenelopeConfig>) =>
    save(basic && leanFollows && typeof patch.maxNetContracts === 'number' ? { ...patch, leanMaxNetContracts: patch.maxNetContracts } : patch);
  return (
    <div className="flex flex-col gap-2 text-xs">
      {/* PEN-01: the basic tier has the side switches instead (usePenelopeSides); lean is an advanced knob now —
          which side's net cap is the lean cap, and which side needs lean-skew less edge. */}
      {tier !== 'basic' && !filtering && (
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground" title="Lean: that side's net cap is the lean cap, and it needs lean-skew less edge to add (the other side more). Which sides she may buy at all is the side switches.">lean</span>
        {leans.map((o) => (
          <button
            key={o.value}
            type="button"
            disabled={!editable || busy}
            aria-pressed={cfg.lean === o.value}
            onClick={() => cfg.lean !== o.value && save({ lean: o.value })}
            className={cn('rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase', cfg.lean === o.value ? 'border-foreground/60 text-foreground' : 'border-border/60 text-muted-foreground hover:text-foreground')}
          >
            {o.label}
          </button>
        ))}
      </div>
      )}
      {basic
        ? groups.flatMap((grp) => grp.knobs).map((k) => <KnobRow key={k.key} k={k} cfg={cfg} editable={editable} save={saveTiered} bound={bounds?.[k.key]} fieldError={errorOf(k.key)} base={base} />)
        : groups.map((grp, i) => {
          // A search or the changed filter opens every group it found something in; otherwise the remembered groups
          // (StreamRift mode), else the first two.
          const open = filtering || (openGroups ? openGroups.has(grp.title) : i < 2);
          const n = changedByGroup?.get(grp.title) ?? 0;
          return (
          <details
            key={grp.title} id={groupDomId(grp.title)} data-group={grp.title} open={open} className="border-border/60 border-t pt-1"
            // The browser toggles the attribute before the event: only a toggle that moved it off the prop is the
            // trader's (the mount and a re-render fire it too, in step with the prop).
            onToggle={onToggleGroup ? (e) => { if (!filtering && e.currentTarget.open !== open) onToggleGroup(grp.title, e.currentTarget.open); } : undefined}
          >
            <summary className="text-muted-foreground cursor-pointer text-[10px] uppercase tracking-wide">
              {grp.title}
              {n > 0 && <span className="ml-1 text-sky-500" aria-label={`${n} changed from your default`}>• {n}</span>}
            </summary>
            {grp.knobs.map((k) => <KnobRow key={k.key} k={k} cfg={cfg} editable={editable} save={save} bound={bounds?.[k.key]} fieldError={errorOf(k.key)} base={base} />)}
          </details>
          );
        })}
      {!basic && !filtering && (
        <details id={PENELOPE_GROUP_VARIANTS} className="border-border/60 border-t pt-1">
          <summary className="text-muted-foreground cursor-pointer text-[10px] uppercase tracking-wide" title="Each variant re-runs this game with something changed, on the same data. Variants are always simulated.">
            Run beside it, simulated ({(cfg.variants ?? []).length} on, max {MAX_VARIANTS})
          </summary>
          <div className="mt-1">
            <VariantPicker variants={cfg.variants ?? []} editable={editable} busy={busy} save={(variants) => save({ variants })} />
          </div>
        </details>
      )}
      {!basic && !filtering && <div id={PENELOPE_GROUP_CLIPBOARD}><ConfigClipboard cfg={cfg} editable={editable} busy={busy} save={save} /></div>}
    </div>
  );
}
